import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router';
import { ErrorBoundary } from 'react-error-boundary';

import { DashboardLayout } from 'src/layouts/dashboard';
import { EventsProvider } from 'src/providers/EventsProvider';
import { ProductsProvider } from 'src/providers/ProductsProvider';
import { insightsNavData } from 'src/layouts/nav-config-insights';
import { StoreLocationsProvider } from 'src/providers/StoreLocationsProvider';
import { ProductModifiersProvider } from 'src/providers/ProductModifiersProvider';
import { ProductCategoriesProvider } from 'src/providers/ProductCategoriesProvider';
import PromotionsSummary from 'src/pages/reports/PromotionsSummary/PromotionsSummary';
import { ProductMainCategoriesProvider } from 'src/providers/ProductMainCategoriesProvider';

import { LoadingScreen } from 'src/components/loading-screen';
import { ErrorPage } from 'src/components/ErrorPage/ErrorPage';

import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';

// ----------------------------------------------------------------------

/** Reports */
const Summary = lazy(() => import('src/pages/reports/Summary/Summary'));
const CustomerTrends = lazy(() => import('src/pages/reports/CustomerTrends/CustomerTrends'));
const StoresOverview = lazy(() => import('src/pages/reports/StoresOverview/StoresOverview'));
const ProductsSummary = lazy(() => import('src/pages/reports/ProductsSummary/ProductsSummary'));
const ItemCoOccurrence = lazy(() => import('src/pages/reports/ItemCoOccurrence/ItemCoOccurrence'));
const CategoriesSummary = lazy(
  () => import('src/pages/reports/CategoriesSummary/CategoriesSummary')
);
const RepeatRates = lazy(() => import('src/pages/reports/RepeatRates/RepeatRates'));
const TrafficSummary = lazy(() => import('src/pages/reports/TrafficSummary/TrafficSummary'));
const AvgMonthlyFrequency = lazy(
  () => import('src/pages/reports/AvgMonthlyFrequency/AvgMonthlyFrequency')
);
const DauWauMauQau = lazy(() => import('src/pages/reports/DauWauMauQau/DauWauMauQau'));
const RepeatCustomersWithinXDays = lazy(
  () => import('src/pages/reports/RepeatCustomersWithinXDays/RepeatCustomersWithinXDays')
);
const AverageLTV = lazy(() => import('src/pages/reports/AverageLTV/AverageLTV'));
const PromoSignUpStats = lazy(() => import('src/pages/reports/PromoSignUpStats/PromoSignUpStats'));
// ----------------------------------------------------------------------

const DashboardProviders = () => {
  const location = useLocation();

  return (
    <DashboardLayout
      slotProps={{
        nav: {
          data: insightsNavData,
        },
        main: {
          sx: {
            height: 'calc(100% - 72px)',
          },
        },
      }}
    >
      <ErrorBoundary resetKeys={[location.pathname]} fallback={<ErrorPage />}>
        <RoleBasedGuard>
          <ProductsProvider>
            <ProductModifiersProvider>
              <StoreLocationsProvider limitUserAccess>
                <ProductMainCategoriesProvider>
                  <ProductCategoriesProvider>
                    <EventsProvider>
                      <Suspense fallback={<LoadingScreen />}>
                        <Outlet />
                      </Suspense>
                    </EventsProvider>
                  </ProductCategoriesProvider>
                </ProductMainCategoriesProvider>
              </StoreLocationsProvider>
            </ProductModifiersProvider>
          </ProductsProvider>
        </RoleBasedGuard>
      </ErrorBoundary>
    </DashboardLayout>
  );
};

export const insightsRoutes: RouteObject[] = [
  {
    path: 'insights',
    element: (
      <AuthGuard>
        <DashboardProviders />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Summary />,
      },
      {
        path: 'customers',
        children: [
          {
            index: true,
            element: <CustomerTrends />,
          },
          {
            path: 'repeat-rates',
            element: <RepeatRates />,
          },
          {
            path: 'traffic-summary',
            element: <TrafficSummary />,
          },
          {
            path: 'active-users',
            element: <DauWauMauQau />,
          },
          {
            path: 'avg-monthly-frequency',
            element: <AvgMonthlyFrequency />,
          },
          {
            path: 'repeat-customers-within-x-days',
            element: <RepeatCustomersWithinXDays />,
          },
          {
            path: 'average-ltv',
            element: <AverageLTV />,
          },
          {
            path: 'promo-signup-stats',
            element: <PromoSignUpStats />,
          },
        ],
      },
      {
        path: 'stores',
        children: [{ index: true, element: <StoresOverview /> }],
      },
      {
        path: 'products',
        children: [
          {
            index: true,
            element: <ProductsSummary />,
          },
          {
            path: 'co-occurrence',
            element: <ItemCoOccurrence />,
          },
        ],
      },
      {
        path: 'categories',
        children: [
          {
            index: true,
            element: <CategoriesSummary />,
          },
        ],
      },
      {
        path: 'promotions',
        children: [
          {
            index: true,
            element: <PromotionsSummary />,
          },
        ],
      },
    ],
  },
];
